import { useNavigate } from '@tanstack/react-router';
import { motion, useScroll, useTransform, useMotionValue, useSpring } from 'framer-motion';
import { AnimatedBlobs } from '@/components/AnimatedBlobs';
import { useEffect, useRef, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faBolt,
  faBook,
  faChartBar,
  faChevronDown,
  faCode,
  faCodeBranch,
  faComment,
  faDesktop,
  faDownload,
  faEye,
  faGlobe,
  faHeartPulse,
  faLayerGroup,
  faLock,
  faMoon,
  faChartLine,
  faFire,
  faRobot,
  faShieldHalved,
  faSun,
  faTerminal,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import { faApple, faWindows, faLinux } from '@fortawesome/free-brands-svg-icons';

// ─── Animated grid background (Stripe-style) ───────────────────────────────
function GridPattern() {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-[1]">
      <svg className="absolute inset-0 w-full h-full opacity-[0.03] dark:opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
            <path d="M 60 0 L 0 0 0 60" fill="none" stroke="currentColor" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>
    </div>
  );
}

// ─── Mouse-tracking gradient orb ────────────────────────────────────────────
function GlowCursor() {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const smoothX = useSpring(mouseX, { stiffness: 50, damping: 20 });
  const smoothY = useSpring(mouseY, { stiffness: 50, damping: 20 });

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      mouseX.set(e.clientX);
      mouseY.set(e.clientY);
    };
    window.addEventListener('mousemove', handleMove);
    return () => window.removeEventListener('mousemove', handleMove);
  }, [mouseX, mouseY]);

  return (
    <motion.div
      className="fixed pointer-events-none z-[2] hidden md:block"
      style={{
        x: smoothX,
        y: smoothY,
        width: 600,
        height: 600,
        marginLeft: -300,
        marginTop: -300,
        background: 'radial-gradient(circle, rgba(236,72,153,0.06) 0%, rgba(139,92,246,0.04) 30%, transparent 70%)',
        filter: 'blur(40px)',
      }}
    />
  );
}

// ─── Animated counter ───────────────────────────────────────────────────────
function AnimatedNumber({ value, suffix = '' }: { value: number; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    let frame: number;
    const duration = 1200;
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setCount(Math.round(eased * value));
      if (t < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [inView, value]);

  return <span ref={ref}>{count}{suffix}</span>;
}

// ─── Feature card with hover glow ───────────────────────────────────────────
function FeatureCard({ icon, title, description, index }: {
  icon: React.ReactNode; title: string; description: string; index: number;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  }, []);

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.6, delay: index * 0.08, ease: [0.21, 0.47, 0.32, 0.98] }}
      onMouseMove={handleMouseMove}
      className="group relative rounded-2xl border border-black/[0.04] dark:border-white/[0.06] bg-white/40 dark:bg-white/[0.02] backdrop-blur-sm overflow-hidden cursor-default hover:border-black/[0.08] dark:hover:border-white/[0.12] transition-colors duration-500"
    >
      {/* Hover glow */}
      <div
        className="absolute w-[300px] h-[300px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          left: mousePos.x - 150,
          top: mousePos.y - 150,
          background: 'radial-gradient(circle, rgba(236,72,153,0.08) 0%, transparent 70%)',
        }}
      />
      <div className="relative p-7">
        <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-slate-100 to-slate-50 dark:from-white/[0.06] dark:to-white/[0.02] border border-black/[0.04] dark:border-white/[0.06] flex items-center justify-center text-slate-700 dark:text-slate-300 mb-5 group-hover:border-pink-200/50 dark:group-hover:border-pink-500/20 transition-colors duration-500">
          {icon}
        </div>
        <h3 className="text-[15px] font-semibold text-slate-900 dark:text-white mb-2 tracking-tight">{title}</h3>
        <p className="text-[13px] text-slate-500 dark:text-slate-400 leading-relaxed">{description}</p>
      </div>
    </motion.div>
  );
}

// ─── Terminal mockup with typing animation ──────────────────────────────────
const TERMINAL_COMMAND = 'open verigit.com/app';

const TERMINAL_LINES: { text: string; className: string; delay: number }[] = [
  { text: '', className: '', delay: 300 },
  { text: '// No install. No sign-up. No backend. Any team.', className: 'text-slate-600', delay: 80 },
  { text: '// Just open your browser and connect your tools.', className: 'text-slate-600', delay: 600 },
  { text: '', className: '', delay: 200 },
  { text: '→ Connecting to Jira, Confluence, Notion, Linear, GitHub...', className: 'text-slate-400', delay: 400 },
  { text: '→ Found 6 platforms · 47 team members · 1,284 events', className: 'text-slate-400', delay: 500 },
  { text: '→ Running 18 performance indicators...', className: 'text-slate-400', delay: 800 },
  { text: '', className: '', delay: 200 },
  { text: '✓ Analysis complete — all data stored in IndexedDB', className: 'text-emerald-400', delay: 300 },
  { text: '✓ Zero bytes sent to any server', className: 'text-emerald-400', delay: 0 },
];

function colorizeTerminalLine(text: string, className: string) {
  if (text.startsWith('→')) {
    // Colorize numbers inside arrow lines
    const parts = text.split(/(\d[\d,]*)/g);
    return (
      <span className={className}>
        {parts.map((part, i) =>
          /^\d[\d,]*$/.test(part)
            ? <span key={i} className={text.includes('performance') ? 'text-pink-400' : text.includes('team members') ? 'text-emerald-400' : 'text-orange-400'}>{part}</span>
            : part.startsWith('→')
              ? <span key={i}><span className="text-purple-400">→</span>{part.slice(1)}</span>
              : <span key={i}>{part}</span>
        )}
      </span>
    );
  }
  return <span className={className}>{text}</span>;
}

function TerminalMockup() {
  const termRef = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState(false);
  const [typedCmd, setTypedCmd] = useState('');
  const [visibleLines, setVisibleLines] = useState(0);
  const [showPrompt, setShowPrompt] = useState(false);
  const hasRun = useRef(false);

  // Trigger animation when terminal scrolls into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !hasRun.current) { hasRun.current = true; setStarted(true); } },
      { threshold: 0.4 }
    );
    if (termRef.current) observer.observe(termRef.current);
    return () => observer.disconnect();
  }, []);

  // Phase 1: Type the command character by character
  useEffect(() => {
    if (!started) return;
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setTypedCmd(TERMINAL_COMMAND.slice(0, i));
      if (i >= TERMINAL_COMMAND.length) clearInterval(interval);
    }, 45);
    return () => clearInterval(interval);
  }, [started]);

  // Phase 2: Stream response lines with staggered delays
  useEffect(() => {
    if (typedCmd.length < TERMINAL_COMMAND.length) return;
    let cumulativeDelay = 400; // initial pause after command

    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let l = 0; l < TERMINAL_LINES.length; l++) {
      cumulativeDelay += TERMINAL_LINES[l].delay;
      const capturedIdx = l + 1;
      timers.push(setTimeout(() => setVisibleLines(capturedIdx), cumulativeDelay));
    }
    // Show final prompt after all lines
    cumulativeDelay += 500;
    timers.push(setTimeout(() => setShowPrompt(true), cumulativeDelay));

    return () => timers.forEach(clearTimeout);
  }, [typedCmd]);

  return (
    <motion.div
      ref={termRef}
      initial={{ opacity: 0, y: 60, rotateX: 8 }}
      whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
      className="relative mx-auto max-w-3xl perspective-[1200px]"
    >
      <div className="rounded-xl border border-black/[0.08] dark:border-white/[0.08] bg-slate-950 shadow-2xl shadow-black/20 overflow-hidden">
        {/* Title bar */}
        <div className="flex items-center gap-2 px-4 py-3 bg-slate-900/80 border-b border-white/[0.04]">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500/80" />
            <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
            <div className="w-3 h-3 rounded-full bg-green-500/80" />
          </div>
          <span className="text-[11px] text-slate-500 font-mono ml-2">verigit — workspace</span>
        </div>
        {/* Animated content */}
        <div className="p-5 font-mono text-[13px] leading-relaxed min-h-[260px]">
          {/* Command line — types out */}
          <div className="text-slate-500">
            <span className="text-emerald-400">$</span>{' '}
            {typedCmd.length > 0 && (
              <>
                {typedCmd.startsWith('open ') ? (
                  <>open <span className="text-cyan-400">{typedCmd.slice(5)}</span></>
                ) : (
                  <>{typedCmd}</>
                )}
              </>
            )}
            {started && typedCmd.length < TERMINAL_COMMAND.length && (
              <span className="inline-block w-2 h-4 bg-emerald-400 animate-pulse align-middle ml-0.5" />
            )}
          </div>

          {/* Response lines — fade in one by one */}
          {TERMINAL_LINES.slice(0, visibleLines).map((line, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -4 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2 }}
              className={line.text === '' ? 'h-3' : (i >= 3 ? 'mt-0' : '')}
            >
              {line.text && colorizeTerminalLine(line.text, line.className)}
            </motion.div>
          ))}

          {/* Final prompt with blinking cursor */}
          {showPrompt && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-3 flex items-center gap-2"
            >
              <span className="text-emerald-400">$</span>
              <span className="inline-block w-2 h-4 bg-emerald-400 animate-pulse" />
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ─── Platform logos ribbon ──────────────────────────────────────────────────
const PLATFORMS = [
  { name: 'Jira', color: '#0052CC' },
  { name: 'GitHub', color: '#333' },
  { name: 'Confluence', color: '#0052CC' },
  { name: 'Notion', color: '#000' },
  { name: 'Linear', color: '#5E6AD2' },
  { name: 'Monday', color: '#FF3D57' },
  { name: 'Trello', color: '#0079BF' },
  { name: 'Bitbucket', color: '#205081' },
  { name: 'GitLab', color: '#FC6D26' },
  { name: 'Google Docs', color: '#4285F4' },
];

// ─── Data ───────────────────────────────────────────────────────────────────
const FEATURES = [
  {
    icon: <FontAwesomeIcon icon={faUsers} className="h-5 w-5 text-emerald-500" />,
    title: 'Every Kind of Team',
    description: 'Dev teams, ops, marketing, content — not just engineers. If your team uses a tool we don\'t support yet, we\'ll add it.',
  },
  {
    icon: <FontAwesomeIcon icon={faLayerGroup} className="h-5 w-5 text-violet-500" />,
    title: 'All Your Platforms',
    description: 'Jira, GitHub, GitLab, Confluence, Notion, Linear, Monday.com, Trello, Bitbucket — unified in one dashboard. More added on request.',
  },
  {
    icon: <FontAwesomeIcon icon={faBook} className="h-5 w-5 text-cyan-500" />,
    title: 'Documentation',
    description: 'Confluence, Notion, and Google Docs — track documentation velocity, collaboration patterns, and knowledge base health across your team.',
  },
  {
    icon: <FontAwesomeIcon icon={faCodeBranch} className="h-5 w-5 text-blue-500" />,
    title: 'Git Analytics',
    description: 'Deep commit analysis, branch tracking, author stats, and diff viewing across any Git repository.',
  },
  {
    icon: <FontAwesomeIcon icon={faBolt} className="h-5 w-5 text-yellow-500" />,
    title: 'Custom Indicators',
    description: 'Build lag/lead performance indicators with sandboxed JavaScript. 18+ built-in, per-platform support.',
  },
  {
    icon: <FontAwesomeIcon icon={faRobot} className="h-5 w-5 text-fuchsia-500" />,
    title: 'Smart Changelog & AI Enrichment',
    description: 'Detect, generate, and AI-enrich changelogs for any repository. Bring your own OpenAI or Anthropic key — commits, Jira, Linear, Confluence and more all woven into a daily changelog, streamed live.',
  },
  {
    icon: <FontAwesomeIcon icon={faComment} className="h-5 w-5 text-rose-500" />,
    title: 'Chat & Messaging',
    description: 'Monitor Slack & Microsoft Teams channels, track communication patterns, and receive daily summaries and anomaly alerts — all client-side, no hosted backend required.',
  },
  {
    icon: <FontAwesomeIcon icon={faChartBar} className="h-5 w-5 text-indigo-500" />,
    title: 'Roadmap & Timeline',
    description: 'Gantt-style roadmap with epics, quick filters, zoom levels, and JQL translation.',
  },
  {
    icon: <FontAwesomeIcon icon={faLock} className="h-5 w-5 text-amber-500" />,
    title: 'Privacy First',
    description: 'Entirely client-side. Credentials never leave your browser. All data in IndexedDB.',
  },
  {
    icon: <FontAwesomeIcon icon={faGlobe} className="h-5 w-5 text-sky-500" />,
    title: 'Zero Backend',
    description: 'WebAssembly Git, IndexedDB storage, stateless CORS proxy. No server, no database.',
  },
  {
    icon: <FontAwesomeIcon icon={faChartLine} className="h-5 w-5 text-teal-500" />,
    title: 'Reports & Analytics',
    description: 'Generate project, individual, cross-project, and Jira PDF reports entirely client-side. Auto-saved report archive, full analysis history, and searchable audit log.',
  },
  {
    icon: <FontAwesomeIcon icon={faFire} className="h-5 w-5 text-orange-500" />,
    title: 'Live Board & Team Dashboard',
    description: 'Real-time commit leaderboard with streak tracking and author stats. Team dashboard aggregates all analyses into a single view with momentum trends and weekly activity.',
  },
];

const SECURITY_POINTS = [
  { icon: <FontAwesomeIcon icon={faEye} className="h-4 w-4" />, label: 'Client-side only', detail: 'All data lives in your browser\'s IndexedDB' },
  { icon: <FontAwesomeIcon icon={faLock} className="h-4 w-4 text-amber-500" />, label: 'AES-GCM encryption', detail: 'Profile exports encrypted with your password' },
  { icon: <FontAwesomeIcon icon={faCode} className="h-4 w-4 text-slate-500" />, label: 'Sandboxed execution', detail: '35+ patterns blocked, 50+ globals shadowed' },
  { icon: <FontAwesomeIcon icon={faHeartPulse} className="h-4 w-4 text-rose-500" />, label: 'Audit logging', detail: 'Every operation timestamped and searchable' },
  { icon: <FontAwesomeIcon icon={faShieldHalved} className="h-4 w-4 text-emerald-500" />, label: 'Host allowlisting', detail: 'CORS proxy forwards to allowlisted hosts only' },
  { icon: <FontAwesomeIcon icon={faTerminal} className="h-4 w-4 text-slate-500" />, label: 'Fully open source', detail: 'Audit every line — proxy code is stateless' },
];

// ─── OS-detecting download button with hover dropdown ────────────────────────
type OSOption = { os: string; label: string; icon: React.ReactNode; url: string; arch?: string };

const DOWNLOAD_OPTIONS: OSOption[] = [
  { os: 'mac-arm64', label: 'macOS (Apple Silicon)', icon: <FontAwesomeIcon icon={faApple} className="h-4 w-4" />, url: 'https://github.com/moderniselife/VeriGit/releases/latest/download/VeriGit-arm64.dmg', arch: 'arm64' },
  { os: 'mac-x64', label: 'macOS (Intel)', icon: <FontAwesomeIcon icon={faApple} className="h-4 w-4" />, url: 'https://github.com/moderniselife/VeriGit/releases/latest/download/VeriGit-x64.dmg', arch: 'x64' },
  { os: 'windows', label: 'Windows', icon: <FontAwesomeIcon icon={faWindows} className="h-4 w-4 text-blue-400" />, url: 'https://github.com/moderniselife/VeriGit/releases/latest/download/VeriGit-Setup.exe' },
  { os: 'linux-appimage', label: 'Linux (AppImage)', icon: <FontAwesomeIcon icon={faLinux} className="h-4 w-4 text-amber-500" />, url: 'https://github.com/moderniselife/VeriGit/releases/latest/download/VeriGit.AppImage' },
  { os: 'linux-deb', label: 'Linux (.deb)', icon: <FontAwesomeIcon icon={faLinux} className="h-4 w-4 text-amber-500" />, url: 'https://github.com/moderniselife/VeriGit/releases/latest/download/VeriGit.deb' },
];

function detectOS(): OSOption {
  const ua = navigator.userAgent.toLowerCase();
  const platform = (navigator as any).userAgentData?.platform?.toLowerCase() || navigator.platform?.toLowerCase() || '';

  if (platform.includes('mac') || ua.includes('macintosh')) {
    // Check for Apple Silicon via WebGL renderer or default to arm64 for modern Macs
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl');
      const renderer = gl?.getExtension('WEBGL_debug_renderer_info');
      const gpu = renderer ? gl?.getParameter(renderer.UNMASKED_RENDERER_WEBGL) : '';
      if (typeof gpu === 'string' && gpu.includes('Apple')) {
        return DOWNLOAD_OPTIONS[0]; // Apple Silicon
      }
    } catch {}
    return DOWNLOAD_OPTIONS[0]; // Default to Apple Silicon
  }
  if (platform.includes('win') || ua.includes('windows')) return DOWNLOAD_OPTIONS[2];
  if (platform.includes('linux') || ua.includes('linux')) return DOWNLOAD_OPTIONS[3];
  return DOWNLOAD_OPTIONS[2]; // Fallback to Windows
}

function DownloadButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [detected] = useState<OSOption>(() => detectOS());
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsOpen(false), 200);
  };

  const otherOptions = DOWNLOAD_OPTIONS.filter(o => o.os !== detected.os);

  return (
    <div
      className="relative z-20"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <a
        href={detected.url}
        className="group flex items-center gap-3 px-8 py-4 text-[15px] font-semibold rounded-xl bg-gradient-to-r from-violet-600 to-pink-500 text-white hover:from-violet-500 hover:to-pink-400 transition-all shadow-lg shadow-violet-500/20 hover:shadow-violet-500/30"
      >
        <FontAwesomeIcon icon={faDownload} className="h-4 w-4" />
        Download for {detected.label.split(' (')[0]}
        <FontAwesomeIcon icon={faChevronDown} className={`h-3.5 w-3.5 opacity-60 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </a>

      {/* Dropdown */}
      <div
        className={`absolute top-full left-0 right-0 mt-2 py-1 rounded-xl bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border border-black/[0.08] dark:border-white/[0.08] shadow-2xl shadow-black/10 transition-all duration-200 origin-top z-50 ${
          isOpen ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'
        }`}
      >
        {otherOptions.map(opt => (
          <a
            key={opt.os}
            href={opt.url}
            className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors first:rounded-t-xl last:rounded-b-xl"
          >
            {opt.icon}
            {opt.label}
          </a>
        ))}
        <div className="border-t border-black/[0.04] dark:border-white/[0.04] mx-2 my-1" />
        <a
          href="https://github.com/moderniselife/VeriGit/releases/latest"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors rounded-b-xl"
        >
          All releases →
        </a>
      </div>
    </div>
  );
}

export function LandingPage() {
  const navigate = useNavigate();
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll();
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.15], [1, 0.96]);
  const [scrolled, setScrolled] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>(() => {
    const stored = localStorage.getItem('verigit-theme');
    if (stored === 'dark' || stored === 'light') return stored;
    return 'system';
  });

  const applyTheme = useCallback((t: 'light' | 'dark' | 'system') => {
    const isDark = t === 'dark' || (t === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    document.documentElement.classList.toggle('dark', isDark);
  }, []);

  // Apply theme on mount and when changed
  useEffect(() => {
    applyTheme(theme);
  }, [theme, applyTheme]);

  // Listen for system preference changes when in system mode
  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyTheme('system');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme, applyTheme]);

  const cycleTheme = () => {
    const next = theme === 'system' ? 'light' : theme === 'light' ? 'dark' : 'system';
    setTheme(next);
    localStorage.setItem('verigit-theme', next);
  };

  // Navbar scroll detection
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 overflow-x-clip relative selection:bg-pink-200 dark:selection:bg-pink-900/40">
      <GlowCursor />
      <GridPattern />

      {/* Rainbow blobs — subtle, far background */}
      <div className="fixed inset-0 pointer-events-none z-0 opacity-60 dark:opacity-40">
        <AnimatedBlobs scale={3.5} />
      </div>

      {/* ==================== NAVBAR ==================== */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-white/70 dark:bg-slate-950/70 backdrop-blur-xl border-b border-black/[0.04] dark:border-white/[0.04]' : ''}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/verigit.png" alt="VeriGit" className="h-7" />
            <span className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight">
              VeriGit
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="https://github.com/moderniselife/VeriGit"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[13px] text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors hidden sm:block"
            >
              GitHub
            </a>
            <button
              onClick={cycleTheme}
              title={`Theme: ${theme}`}
              className="relative h-8 w-8 flex items-center justify-center rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              {theme === 'system' ? (
                <FontAwesomeIcon icon={faDesktop} className="h-4 w-4 text-slate-400" />
              ) : theme === 'dark' ? (
                <FontAwesomeIcon icon={faMoon} className="h-4 w-4 text-indigo-400" />
              ) : (
                <FontAwesomeIcon icon={faSun} className="h-4 w-4 text-amber-400" />
              )}
            </button>
            <button
              onClick={() => navigate({ to: '/app' })}
              className="px-4 py-2 text-[13px] font-medium rounded-lg bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-100 transition-colors"
            >
              Open App →
            </button>
          </div>
        </div>
      </nav>

      {/* ==================== HERO ==================== */}
      <section ref={heroRef} className="relative z-20 max-w-5xl mx-auto px-6 pt-32 pb-8 md:pt-44 md:pb-16">
        <motion.div style={{ opacity: heroOpacity, scale: heroScale }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.21, 0.47, 0.32, 0.98] }}
            className="text-center"
          >
            {/* Pill badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-black/[0.06] dark:border-white/[0.06] mb-10"
            >
              <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400 tracking-wide uppercase">Open Source · Any Team · Zero Backend</span>
            </motion.div>

            {/* Headline */}
            <h1 className="text-5xl sm:text-6xl md:text-[80px] font-extrabold tracking-[-0.04em] leading-[0.95] mb-8">
              <span className="text-slate-900 dark:text-white">Team</span>
              <br />
              <span className="text-slate-900 dark:text-white">analytics </span>
              <span className="bg-gradient-to-r from-orange-500 via-pink-500 to-violet-600 bg-clip-text text-transparent">
                without
              </span>
              <br />
              <span className="bg-gradient-to-r from-violet-600 via-pink-500 to-orange-500 bg-clip-text text-transparent">
                the backend.
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-slate-500 dark:text-slate-400 max-w-2xl mx-auto mb-12 leading-relaxed font-light">
              Track project activity, team health, documentation, roadmap timelines, and performance — for every kind of team, entirely in your browser.
            </p>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => navigate({ to: '/app' })}
                className="group flex items-center gap-3 px-8 py-4 text-[15px] font-semibold rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-100 transition-all shadow-lg shadow-slate-900/10 dark:shadow-white/10"
              >
                Open in Browser
                <FontAwesomeIcon icon={faArrowRight} className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </button>
              {false && <DownloadButton />}
              <a
                href="https://github.com/moderniselife/VeriGit"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-8 py-4 text-[15px] font-medium rounded-xl text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors"
              >
                View on GitHub
                <FontAwesomeIcon icon={faArrowRight} className="h-4 w-4" />
              </a>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* ==================== TERMINAL ==================== */}
      <section className="relative z-10 px-6 pb-20 md:pb-32">
        <TerminalMockup />
      </section>

      {/* ==================== PLATFORM RIBBON ==================== */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 pb-20 md:pb-32">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <p className="text-[11px] font-medium text-slate-400 dark:text-slate-500 uppercase tracking-[0.15em] mb-8">
            Works with the tools you already use
          </p>
          <div className="flex items-center justify-center gap-8 md:gap-12 flex-wrap">
            {PLATFORMS.map((p, i) => (
              <motion.span
                key={p.name}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="text-sm font-medium text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors cursor-default"
              >
                {p.name}
              </motion.span>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ==================== STATS ==================== */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 pb-20 md:pb-32">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
          {[
            { value: 10, suffix: '+', label: 'Platforms' },
            { value: 18, suffix: '+', label: 'Indicators' },
            { value: 0, suffix: '', label: 'Servers', display: 'Zero' },
            { value: 100, suffix: '%', label: 'Open Source' },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="text-center"
            >
              <div className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">
                {stat.display || <AnimatedNumber value={stat.value} suffix={stat.suffix} />}
              </div>
              <div className="text-[13px] text-slate-400 dark:text-slate-500 mt-2">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ==================== FEATURES ==================== */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 pb-20 md:pb-32">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-slate-900 dark:text-white mb-4">
            Everything you need.
          </h2>
          <p className="text-slate-500 dark:text-slate-400 max-w-lg mx-auto text-lg font-light">
            Platform-agnostic analytics with deep integrations, all running client-side.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((feature, i) => (
            <FeatureCard key={feature.title} {...feature} index={i} />
          ))}
        </div>
      </section>

      {/* ==================== SECURITY ==================== */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 pb-20 md:pb-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left — text */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white mb-5">
              Security you<br />can verify.
            </h2>
            <p className="text-slate-500 dark:text-slate-400 leading-relaxed mb-4">
              No trust required. API tokens pass through a stateless CORS proxy that never reads, logs, or stores them. Custom indicator code runs in a two-layer sandbox.
            </p>
            <p className="text-[13px] text-slate-400 dark:text-slate-500 leading-relaxed">
              Architected with SOC 2 principles in mind. Not yet independently audited — we believe in radical precision over certification cosplay.
            </p>
          </motion.div>

          {/* Right — security points */}
          <div className="grid gap-3">
            {SECURITY_POINTS.map((point, i) => (
              <motion.div
                key={point.label}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.5 }}
                className="flex items-start gap-4 p-4 rounded-xl border border-transparent hover:border-black/[0.04] dark:hover:border-white/[0.06] hover:bg-slate-50/50 dark:hover:bg-white/[0.01] transition-all group"
              >
                <div className="h-8 w-8 rounded-lg bg-slate-100 dark:bg-white/[0.04] flex items-center justify-center text-slate-500 dark:text-slate-400 flex-shrink-0 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                  {point.icon}
                </div>
                <div>
                  <div className="text-[13px] font-medium text-slate-900 dark:text-white">{point.label}</div>
                  <div className="text-[12px] text-slate-400 dark:text-slate-500">{point.detail}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== FINAL CTA ==================== */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 pb-32 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-slate-900 dark:text-white mb-6">
            Ready to ship?
          </h2>
          <p className="text-lg text-slate-500 dark:text-slate-400 mb-10 font-light">
            No sign-up. No credit card. No backend.<br className="hidden sm:block" />
            Just open your browser.
          </p>
          <button
            onClick={() => navigate({ to: '/app' })}
            className="group inline-flex items-center gap-3 px-10 py-4 text-lg font-semibold rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-100 transition-all shadow-lg shadow-slate-900/10 dark:shadow-white/10"
          >
            Launch VeriGit
            <FontAwesomeIcon icon={faArrowRight} className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>
      </section>

      {/* ==================== FOOTER ==================== */}
      <footer className="relative z-10 border-t border-black/[0.04] dark:border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/verigit.png" alt="VeriGit" className="h-4 opacity-50" />
            <span className="text-[13px] text-slate-400 dark:text-slate-500">
              VeriGit · Open Source · MIT License
            </span>
          </div>
          <div className="flex items-center gap-6 text-[13px] text-slate-400 dark:text-slate-500">
            <a href="https://github.com/moderniselife/VeriGit" target="_blank" rel="noopener noreferrer" className="hover:text-slate-700 dark:hover:text-slate-200 transition-colors">
              GitHub
            </a>
            <a href="https://github.com/moderniselife/VeriGit/blob/main/CHANGELOG.md" target="_blank" rel="noopener noreferrer" className="hover:text-slate-700 dark:hover:text-slate-200 transition-colors">
              Changelog
            </a>
            <a href="https://github.com/moderniselife/VeriGit/blob/main/LICENSE" target="_blank" rel="noopener noreferrer" className="hover:text-slate-700 dark:hover:text-slate-200 transition-colors">
              License
            </a>
            <a href="/PRIVACY.md" target="_blank" rel="noopener noreferrer" className="hover:text-slate-700 dark:hover:text-slate-200 transition-colors">
              Privacy
            </a>
            <a href="/TERMS.md" target="_blank" rel="noopener noreferrer" className="hover:text-slate-700 dark:hover:text-slate-200 transition-colors">
              Terms
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
